import type { TagTone } from '@dayon/design-system';

/* ── Tabs ───────────────────────────────────────────────────────────────────
   The design's URL contract is `players/<id>/<tab>`, e.g. players/PL-10241/
   activity. The console's route table registers `/players/:id` with no tab
   segment, and that table is shared with the sidebar, so the tab is carried as
   a query parameter instead — which is also the contract Tabs documents. Same
   guarantee either way: the tab survives a refresh and pastes into a support
   ticket. The keys are the design's lowercased labels, unchanged, plus `security`. */
export const TABS = [
  { key: 'activity', label: 'Activity' },
  { key: 'kyc', label: 'KYC' },
  { key: 'balances', label: 'Balances' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'bonuses', label: 'Bonuses' },
  { key: 'points', label: 'Points' },
  { key: 'restrictions', label: 'Restrictions' },
  /*
   * ADDED 8 October 2026, and it is the door onto `player-admin/routes.ts` —
   * four routes that had been built, audited and reason-coded for weeks with
   * nothing in any front end calling them. Until this tab the console's only
   * answer to "somebody else is in my account" was to suspend it, which bars
   * the owner and leaves the attacker's live session running.
   */
  { key: 'security', label: 'Security' },
  { key: 'logs', label: 'Logs' },
] as const;

export type TabKey = (typeof TABS)[number]['key'];

export const isTabKey = (value: string): value is TabKey => TABS.some((t) => t.key === value);

export const TIER_LABEL: Record<string, string> = {
  BRONZE: 'Bronze',
  SILVER: 'Silver',
  GOLD: 'Gold',
  VIP_3: 'VIP 3',
  VIP_4: 'VIP 4',
};

/** Status and KYC are separate dimensions; this screen never collapses them. */
export const STATUS_TONE: Record<string, TagTone> = {
  ACTIVE: 'accent',
  FLAGGED: 'outline',
  RESTRICTED: 'quiet',
  SUSPENDED: 'quiet',
};

export const KYC_TONE: Record<string, TagTone> = {
  VERIFIED: 'neutral',
  PENDING: 'outline',
  REJECTED: 'quiet',
  UNVERIFIED: 'quiet',
};

/** Sentence-cased for display; the stored value is the upper-case enum member. */
export const sentence = (enumValue: string): string =>
  enumValue.charAt(0) + enumValue.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * The five account controls, in the design's order, with its copy verbatim.
 *
 * They map 1:1 onto player_restriction.kind, so a control with no matching row
 * is genuinely Off — an absent restriction is a fact, not a missing figure,
 * which is why this table is rendered live rather than named as a gap.
 */
export const CONTROLS = [
  {
    kind: 'WITHDRAWAL_BLOCK',
    label: 'Withdrawal block',
    description: 'Blocks new withdrawal requests; existing ones continue',
  },
  {
    kind: 'DEPOSIT_LIMIT',
    label: 'Deposit limit',
    description: 'Player-requested limit, 24h cooling-off to raise',
  },
  {
    kind: 'BONUS_EXCLUSION',
    label: 'Bonus exclusion',
    description: 'Player cannot opt into any promotion',
  },
  {
    kind: 'LIVE_RESTRICTED',
    label: 'Live casino restricted',
    description: 'Blocks Evolution and in-house live tables',
  },
  {
    kind: 'SELF_EXCLUSION',
    label: 'Self-exclusion',
    description: 'Full account lock — cannot be lifted by staff',
  },
] as const;

export const LIFT_REASONS = [
  { code: 'REVIEW_CLEARED', label: 'Review cleared' },
  { code: 'APPLIED_IN_ERROR', label: 'Applied in error' },
  { code: 'PLAYER_APPEAL_UPHELD', label: 'Player appeal upheld' },
  { code: 'DOCUMENTS_RECEIVED', label: 'Documents received' },
  { code: 'REGULATORY_ORDER_LIFTED', label: 'Regulatory order lifted' },
] as const;

/**
 * The reasons an older KYC submission could be refused for, in words. Nothing refuses one since
 * that process closed on 23 October 2026; the older-submissions list reads a refusal's reason here.
 */
export const KYC_REJECTION_REASONS = [
  { code: 'DOCUMENT_UNREADABLE', label: 'Document unreadable' },
  { code: 'DOCUMENT_EXPIRED', label: 'Document expired' },
  { code: 'NAME_MISMATCH', label: 'Name does not match' },
  { code: 'DATE_OF_BIRTH_MISMATCH', label: 'Date of birth does not match' },
  { code: 'ADDRESS_UNVERIFIED', label: 'Address unverified' },
  { code: 'SELFIE_MISMATCH', label: 'Selfie does not match the document' },
  { code: 'SUSPECTED_FORGERY', label: 'Suspected forgery' },
  { code: 'DUPLICATE_ACCOUNT', label: 'Duplicate account' },
] as const;

/** The rule the database enforces, in the words an operator can act on. */
export const SELF_EXCLUSION_RULE =
  'A player self-exclusion is a full account lock that staff cannot lift — the platform itself refuses the lift, whatever asks for it. It expires on its own terms.';

/**
 * An older submission's status: APPROVED neutral, everything else quiet. PENDING was outlined,
 * the console's "waiting for you" look; since the process closed (23 Oct 2026) nothing waits on
 * one, and the row says it was never decided.
 */
export const KYC_SUBMISSION_TONE: Record<string, TagTone> = {
  PENDING: 'quiet',
  APPROVED: 'neutral',
  REJECTED: 'quiet',
  RESUBMIT: 'quiet',
};

/** Banded exactly as the players list bands it, so one player reads the same
    on both screens. The band is derived presentation; only the score is stored. */
export const riskBand = (score: number): string =>
  score >= 70 ? 'High' : score >= 40 ? 'Medium' : 'Low';
